import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { useStaggerIn } from "@/components/Reveal";

export interface Project {
  title: string;
  description: string;
  tech: string[];
  thumbnail?: string;
  video?: string;
  link?: string;
  date?: string;
}

export const allProjects: Project[] = [
  {
    title: "Vexel",
    description: "An image or a clip goes in, editable vector artwork comes out — every region its own path, in its original colour.",
    tech: ["React", "SVG", "Lottie", "FFmpeg"],
    // 5s looping recording of the live site (animated WebP, loops on its own)
    thumbnail: "/projects/vexel-loop.webp",
    link: "https://vexel.chiraggadhvi.in/",
    date: "Sep 2026"
  },
  {
    title: "Try This Daily",
    description: "Make the video, posting is handled — upload reels, write captions once, and they go out to Instagram, YouTube, Facebook and Threads on schedule from the cloud.",
    tech: ["React", "OAuth", "Cloud Scheduler", "Supabase"],
    video: "/projects/trythisdaily.mp4",
    link: "https://trythisdaily.chiraggadhvi.in/",
    date: "Sep 2026"
  },
  {
    title: "PathIQ",
    description: "A path-tracing puzzle game — build an expression that hits the exact target. Daily challenges, endless mode.",
    tech: ["React", "TypeScript", "TailwindCSS"],
    thumbnail: "/src/assets/Projects/PathIQ.webp",
    link: "https://pathsiq.vercel.app/",
    date: "Aug 2026"
  },
  {
    title: "InterviewEdge",
    description: "An Electron app that discreetly assists with coding challenges during live technical interviews.",
    tech: ["Electron", "React", "Node.js", "AI Models"],
    thumbnail: "/src/assets/Projects/interviewedge-site.webp",
    link: "https://interviewedge-one.vercel.app/",
    date: "May 2026"
  },
  {
    title: "AdPack",
    description: "An AI media engine that turns one product photo into professional Amazon listing shots and UGC videos.",
    tech: ["KIE API", "Open Router", "ChatGPT 2", "Kling", "Nano Banana"],
    thumbnail: "/src/assets/Projects/AdPack.webp",
    link: "https://adpack.chiraggadhvi.in/",
    date: "May 2026"
  },
  {
  title: "ChiragAnswers",
  description: "An AI twist on Peter Answers — generates clever, funny, or spooky replies from hidden inputs.",
  tech: ["Next.js", "OpenAI API"],
  thumbnail: "/src/assets/Projects/ChiragAnswers.webp",
  link: "https://chiraganswers.vercel.app/",
  date: "Apr 2026"
},
  {
    title: "LinguaAid",
    description: "AI-powered document simplification, breaking language barriers for immigrants and refugees.",
    tech: ["Next.js", "Supabase", "Lingo.dev"],
    thumbnail: "/src/assets/Projects/LinguaAid.webp",
    link: "https://linguaaid.chiraggadhvi.in/",
    date: "Feb 2026"
  },
  {
    title: "GifDuo – Create Professional Two-Photo GIFs 🎞️",
    description: "Turns two photos into a polished GIF — perfect for email signatures and social profiles.",
    tech: ["React", "GIF.js", "Canvas API", "Framer Motion", "Stunning UI"],
    thumbnail: "/src/assets/Projects/gifduo.webp",
    link: "https://gifduo.vercel.app/",
    date: "Jan 2026"
  },
  {
    title: "AI-Powered Portfolio Platform",
    description: "This site, enhanced — AI chatbot, voice assistant, community prompt gallery, and Shorts integration.",
    tech: ["Next.js", "FastAPI", "OpenAI", "Voice AI", "RAG"],
    thumbnail: "/src/assets/Projects/chirag.webp",
    link: "https://www.chiraggadhvi.in",
    date: "Dec 2025"
  },
  {
    title: "Coincentrate – Your Personal Productivity Coin Tracker",
    description: "A dark-mode focus tracker that turns your sessions into visual, motivating coins.",
    tech: ["Next.js", "Vite", "TailwindCSS", "Sanity"],
    thumbnail: "/src/assets/Projects/coincentrate1.webp",
    link: "https://coincentrate.vercel.app",
    date: "October 2025"
  },
];

interface ProjectsProps {
  maxProjects?: number;
  showViewAll?: boolean;
}

// Map all project images to their built URLs so we can reference them dynamically
const projectImages = import.meta.glob('/src/assets/Projects/*', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

export const resolveThumbnail = (inputPath?: string): string | undefined => {
  if (!inputPath) return undefined;
  const inputFileName = inputPath.split('/').pop();
  if (!inputFileName) return undefined;
  for (const [key, value] of Object.entries(projectImages)) {
    if (key.endsWith(inputFileName)) return value;
  }
  return undefined;
};

// Autoplaying video that only downloads once it scrolls into view
export function LazyVideo({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { rootMargin: "200px" });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      src={src}
      loop
      muted
      playsInline
      preload="none"
      className="relative z-[1] h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
    />
  );
}

export function Projects({ maxProjects, showViewAll = false }: ProjectsProps) {
  const location = useLocation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const scope = useStaggerIn<HTMLElement>();
  const stagger = maxProjects ? {} : { "data-stagger": "" };



  const displayedProjects = maxProjects ? allProjects.slice(0, maxProjects) : allProjects;

  // Scroll to title if hash matches
  useEffect(() => {
    if (location.hash === "#featured-projects" && titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  return (
    <section
      ref={scope}
      id="projects"
      className="py-6 md:py-8 px-4 sm:px-6 bg-background"
    >
      <div className="site-frame">
        {/* Section header on the home page; page title + subtitle (like Blogs) on /projects */}
        {maxProjects ? (
          <div className="mb-4 md:mb-5 text-left">
            <h2
              id="featured-projects"
              ref={titleRef}
              className="text-[15px] md:text-base font-semibold tracking-tight text-foreground"
            >
              Projects
            </h2>
          </div>
        ) : (
          <div className="mb-8 sm:mb-10">
            <h1
              {...stagger}
              id="featured-projects"
              ref={titleRef}
              className="mb-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
            >
              Projects
            </h1>
            <p {...stagger} className="text-muted-foreground">Things I've built: web apps, AI tools and a few fun experiments.</p>
          </div>
        )}

        {/* Projects Gallery */}
        {/* Projects Gallery — same card + hover as BlogCard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-10 mb-10">
          {displayedProjects.map((project) => (
            <a
              {...stagger}
              key={project.title}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col"
            >
              <div className="shimmer aspect-video w-full rounded-2xl border border-border [transform:translateZ(0)]">
                {project.video ? (
                  <LazyVideo src={project.video} />
                ) : project.thumbnail ? (
                  <img
                    src={resolveThumbnail(project.thumbnail) ?? project.thumbnail}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="relative z-[1] h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                    onError={(e) => {
                      const fallback = resolveThumbnail('chiraggadhvi.webp');
                      if (fallback && e.currentTarget.src !== fallback) {
                        e.currentTarget.src = fallback;
                      }
                    }}
                  />
                ) : null}
              </div>
              <h3 className="mt-3 text-sm md:text-[15px] font-semibold leading-snug tracking-tight text-foreground">
                {project.title}
              </h3>
              <div className="mt-auto pt-1.5 flex items-center justify-between gap-3 text-xs md:text-[13px] text-muted-foreground">
                <span>{project.date}</span>
                <span className="truncate">{project.tech.slice(0, 2).join(" · ")}</span>
              </div>
            </a>
          ))}
        </div>

        {/* View All Button */}
        {maxProjects && (
          <div className="flex justify-center">
            <Link
              to="/projects#featured-projects"
              className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              View all projects
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
